/* Motion input for the existing holo-card renderers — approved-motion-20260911.
   iOS requests begin only inside the explicit 3D Preview button click.
   No sensor recording, network requests or purchase-link interception.
   Creative inspiration: @乌托邦的香蕉 (Xiaohongshu / Bilibili). */
import { phoneMotionMedia as phone, getCardMotionProfile } from './loc-motion-profiles.js?v=approved-20260911';
const reduced = matchMedia('(prefers-reduced-motion: reduce)');
document.documentElement.dataset.locMotionProfile = 'approved-20260911';
const cards = new Set(), visible = new Set(), touches = new Set(), hints = new Map(), buttons = new Map();
let enabled = false, optedIn = false, requesting = false, denied = false, disposed = false;
let permissions = null, current = null, origin = null, source = '';
let frame = 0, lastPaint = 0, releaseTimer = 0, sensorTimer = 0, orientationAt = -Infinity;
let targetX = 0, targetY = 0, smoothX = 0, smoothY = 0, lastX = NaN, lastY = NaN;
const wrap = value => ((value + 180) % 360 + 360) % 360 - 180;
const clamp = (value, limit) => Math.max(-limit, Math.min(limit, value));
const messages = {
  'gesture-required': ['Tap 3D Preview, then allow motion to tilt this card.', '点击“3D 预览”并允许运动权限，即可倾斜手机预览。'],
  requesting: ['Allow Motion & Orientation in the system prompt.', '请在系统弹窗中允许访问运动与方向。'],
  waiting: ['Motion allowed — gently tilt your phone.', '运动权限已允许，轻轻倾斜手机即可。'],
  active: ['Tilt your phone to explore the foil.', '倾斜手机，感受闪卡光影。'],
  denied: ['Motion access was denied. Check this site’s motion permission and reload. Touch preview still works.', '运动权限未允许。请检查此网站权限后刷新；仍可用手指预览。'],
  'permission-error': ['Tap 3D Preview to retry. If using an in-app browser, open this page in Safari.', '点击“3D 预览”重试授权；若使用 App 内置浏览器，请改用 Safari 打开。'],
  'no-sensor': ['No motion data received. Try Safari and check motion permission; touch preview still works.', '暂未收到运动数据，请尝试 Safari 并检查权限；仍可用手指预览。'],
  'https-required': ['Motion preview needs a secure HTTPS link.', '请使用 HTTPS 安全链接体验手机倾斜预览。'],
  unavailable: ['Motion is unavailable in this browser. Try Safari or use touch preview.', '此浏览器未提供运动传感器，请尝试 Safari 或手指预览。'],
  reduced: ['Motion effects are paused by your Reduce Motion setting.', '陀螺仪效果已根据系统“减少动态效果”设置暂停。'],
};
function updateHint(hint, value) {
  const message = messages[value];
  hint.hidden = !phone.matches || !message;
  const text = message?.[/^zh/i.test(document.documentElement.lang) ? 1 : 0] || '';
  if (hint.textContent !== text) hint.textContent = text;
}
function updateButton(button) {
  const zh = /^zh/i.test(document.documentElement.lang);
  button.hidden = !phone.matches;
  button.disabled = requesting;
  button.setAttribute('aria-pressed', String(enabled));
  button.setAttribute('aria-busy', String(requesting));
  const label = requesting ? (zh ? '正在开启…' : 'Opening…')
    : enabled ? (zh ? '关闭 3D 预览' : 'Exit 3D Preview') : (zh ? '3D 预览' : '3D Preview');
  const text = button.querySelector('span');
  if (text.textContent !== label) text.textContent = label;
}
function status(value) {
  if (document.documentElement.dataset.locMotionState === value) return;
  document.documentElement.dataset.locMotionState = value;
  hints.forEach(hint => updateHint(hint, value));
  buttons.forEach(updateButton);
}
function clearCard() {
  if (current) {
    current.classList.remove('is-motion-active');
    if (!current.classList.contains('is-pointer-active')) {
      for (const name of ['--ip-tilt-x', '--ip-tilt-y', '--ip-foil-x', '--ip-foil-y']) current.style.removeProperty(name);
    }
  }
  current = null; lastX = lastY = NaN;
}
function calibrate() {
  origin = null; source = ''; orientationAt = -Infinity;
  targetX = targetY = smoothX = smoothY = lastPaint = 0;
  cancelAnimationFrame(frame); frame = 0;
  clearCard();
}
function selectCard() {
  const card = [...visible].find(card => card.isConnected && card.closest('[data-ip-panel]')?.classList.contains('is-active')) || null;
  if (card !== current) { calibrate(); current = card; }
  return card;
}
function canMove() {
  return enabled && !disposed && !document.hidden && !touches.size && !reduced.matches && phone.matches;
}
function paint(now) {
  frame = 0;
  if (!canMove() || !current || !visible.has(current) || !current.closest('[data-ip-panel]')?.classList.contains('is-active') || current.classList.contains('is-pointer-active')) return;
  // Limit DOM/renderer input to ~60Hz, smooth noisy sensors, and stop when settled.
  if (lastPaint && now - lastPaint < 16) { frame = requestAnimationFrame(paint); return; }
  const dt = lastPaint ? Math.min(now - lastPaint, 50) : 16;
  lastPaint = now;
  const response = getCardMotionProfile(current);
  const blend = 1 - Math.exp(-dt / response.smoothing);
  smoothX += (targetX - smoothX) * blend;
  smoothY += (targetY - smoothY) * blend;
  const settled = Math.abs(targetX - smoothX) + Math.abs(targetY - smoothY) < .025;
  if (settled) { smoothX = targetX; smoothY = targetY; }
  const x = Math.round(smoothX * 50) / 50, y = Math.round(smoothY * 50) / 50;
  if (x !== lastX || y !== lastY) {
    lastX = x; lastY = y;
    current.style.setProperty('--ip-tilt-x', x + 'deg');
    current.style.setProperty('--ip-tilt-y', y + 'deg');
    current.style.setProperty('--ip-foil-x', (50 + clamp(y * 5, 50)) + '%');
    current.style.setProperty('--ip-foil-y', (50 - clamp(x * 6.25, 50)) + '%');
    current.classList.add('is-motion-active');
  }
  if (!settled) frame = requestAnimationFrame(paint);
}
function orient(beta, gamma, kind) {
  if (!canMove() || !Number.isFinite(beta) || !Number.isFinite(gamma)) return;
  clearTimeout(sensorTimer);
  // Choose the active visible card BEFORE establishing the neutral phone pose.
  if (!selectCard()) return;
  if (kind !== source) { origin = null; source = kind; }
  if (!origin) origin = { beta, gamma };
  const b = wrap(beta - origin.beta), g = wrap(gamma - origin.gamma);
  const angle = (screen.orientation?.angle ?? window.orientation ?? 0) * Math.PI / 180;
  const response = getCardMotionProfile(current);
  targetX = Math.round(clamp(-(b * Math.cos(angle) + g * Math.sin(angle)) * response.gainX, response.limitX) * 50) / 50;
  targetY = Math.round(clamp((g * Math.cos(angle) - b * Math.sin(angle)) * response.gainY, response.limitY) * 50) / 50;
  status('active');
  if (!frame) frame = requestAnimationFrame(paint);
}
function onOrientation(event) {
  if (!Number.isFinite(event.beta) || !Number.isFinite(event.gamma)) return;
  orient(event.beta, event.gamma, 'orientation');
  orientationAt = performance.now();
}
function onGravity(event) {
  if (performance.now() - orientationAt < 1000) return;
  const g = event.accelerationIncludingGravity;
  if (!g || ![g.x, g.y, g.z].every(Number.isFinite) || Math.hypot(g.x, g.y, g.z) < 1) return;
  orient(Math.atan2(-g.y, Math.hypot(g.x, g.z)) * 180 / Math.PI, Math.atan2(g.x, -g.z) * 180 / Math.PI, 'gravity');
}
function detachSensors() {
  removeEventListener('deviceorientation', onOrientation);
  removeEventListener('devicemotion', onGravity);
  clearTimeout(sensorTimer);
}
function start(allowed) {
  if (disposed || !optedIn || !phone.matches || reduced.matches) return;
  detachSensors();
  permissions = allowed; enabled = true; calibrate(); status('waiting');
  if (allowed[0]) addEventListener('deviceorientation', onOrientation, { passive: true });
  if (allowed[1]) addEventListener('devicemotion', onGravity, { passive: true });
  sensorTimer = setTimeout(() => { if (document.documentElement.dataset.locMotionState === 'waiting') status('no-sensor'); }, 6000);
}
function attempt(fromGesture = false) {
  if (disposed || requesting) return;
  if (!phone.matches) { status('desktop'); return; }
  if (reduced.matches) { status('reduced'); return; }
  if (enabled) return;
  if (!isSecureContext) { status('https-required'); return; }
  if (denied) { status('denied'); return; }
  const apis = [globalThis.DeviceOrientationEvent, globalThis.DeviceMotionEvent];
  if (!apis.some(Boolean)) { status('unavailable'); return; }
  // Even browsers without a permission prompt wait for the explicit button.
  if (!fromGesture && !optedIn) { status('gesture-required'); return; }
  optedIn = true;
  if (permissions?.some(Boolean)) { start(permissions); return; }
  requesting = true; status('requesting');
  // Start both native permission calls synchronously, BEFORE any await or timer.
  const requests = apis.map(api => {
    if (!api) return Promise.resolve('unavailable');
    try { return typeof api.requestPermission === 'function' ? api.requestPermission() : Promise.resolve('granted'); }
    catch (error) { return Promise.reject(error); }
  });
  Promise.allSettled(requests).then(results => {
    const allowed = results.map(r => r.status === 'fulfilled' && r.value === 'granted');
    requesting = false;
    buttons.forEach(updateButton);
    if (allowed.some(Boolean)) {
      permissions = allowed;
      if (!disposed) {
        if (optedIn && phone.matches && !reduced.matches) start(allowed);
        else status(reduced.matches ? 'reduced' : phone.matches ? 'gesture-required' : 'desktop');
      }
    } else {
      optedIn = false;
      denied = results.some(r => r.status === 'fulfilled' && r.value === 'denied');
      if (!disposed) status(denied ? 'denied' : 'permission-error');
    }
  });
}
const observer = new IntersectionObserver(entries => {
  for (const e of entries) {
    if (e.isIntersecting && e.intersectionRatio >= .15) visible.add(e.target);
    else visible.delete(e.target);
  }
  if (current && !visible.has(current)) calibrate();
}, { threshold: [0, .15] });
function scan(root = document) {
  root.querySelectorAll('[data-ip-collectible]').forEach(card => {
    if (cards.has(card)) return;
    cards.add(card); observer.observe(card);
    const spotlight = card.closest('.loc-ip__spotlight');
    if (spotlight && !hints.has(spotlight)) {
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'loc-ip__motion-button';
      button.setAttribute('data-loc-motion-toggle', '');
      button.hidden = true;
      button.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="m12 3 9 5v8l-9 5-9-5V8l9-5Z M3 8l9 5 9-5 M12 13v8 M7.5 5.5l9 5"/></svg><span>3D Preview</span>';
      const hint = document.createElement('p');
      hint.className = 'loc-ip__motion-hint'; hint.hidden = true;
      hint.setAttribute('role', 'status'); hint.setAttribute('aria-live', 'polite');
      spotlight.append(button, hint); hints.set(spotlight, hint); buttons.set(spotlight, button);
      updateHint(hint, document.documentElement.dataset.locMotionState);
      updateButton(button);
    }
  });
  attempt();
}
function down(event) {
  if (event.pointerType !== 'touch') return;
  touches.add(event.pointerId); clearTimeout(releaseTimer); calibrate();
}
function up(event) {
  touches.delete(event.pointerId);
  if (!touches.size) releaseTimer = setTimeout(calibrate, 80);
}
function previewClick(event) {
  if (!event.isTrusted || !event.target.closest('.loc-home [data-loc-motion-toggle]')) return;
  if (enabled) {
    optedIn = false; enabled = false; detachSensors(); calibrate(); status('gesture-required');
  } else attempt(true);
}
document.addEventListener('click', previewClick, true);
document.addEventListener('pointerdown', down, { capture: true, passive: true });
document.addEventListener('pointerup', up, { capture: true, passive: true });
document.addEventListener('pointercancel', up, { capture: true, passive: true });
document.addEventListener('visibilitychange', () => { touches.clear(); calibrate(); });
screen.orientation?.addEventListener('change', calibrate);
addEventListener('orientationchange', calibrate);
function settingsChanged() {
  enabled = false; detachSensors(); calibrate();
  if (optedIn && phone.matches && !reduced.matches && permissions) start(permissions);
  else attempt();
}
phone.addEventListener('change', settingsChanged);
reduced.addEventListener('change', settingsChanged);
document.addEventListener('shopify:section:load', event => scan(event.target));
document.addEventListener('shopify:section:unload', event => {
  for (const card of cards) if (event.target.contains(card)) {
    if (card === current) calibrate();
    observer.unobserve(card); visible.delete(card); cards.delete(card);
  }
  for (const [spotlight, hint] of hints) if (event.target.contains(spotlight)) {
    hint.remove(); hints.delete(spotlight); buttons.get(spotlight)?.remove(); buttons.delete(spotlight);
  }
});
addEventListener('pagehide', () => {
  disposed = true; enabled = false; touches.clear(); clearTimeout(releaseTimer); detachSensors(); calibrate();
});
addEventListener('pageshow', event => {
  if (!event.persisted) return;
  disposed = false; touches.clear(); calibrate();
  if (optedIn && permissions && phone.matches && !reduced.matches) start(permissions);
  else attempt();
});
scan();
